import { useEffect, useState } from "react";
import { Save, DollarSign, Receipt, Calculator, TrendingUp } from "lucide-react";
import AdminShell, { Panel, Btn } from "@/components/admin/AdminShell";
import KpiCard from "@/components/admin/KpiCard";
import { PrimaryTabs, SecondaryTabs } from "@/components/admin/SubTabs";

import { api } from "@/lib/api";

const TABS = [
  { id: "tax", label: "Tax Management" },
  { id: "gst", label: "GST Tracking" },
];
const SUBTABS = [
  { id: "dashboard", label: "Dashboard" },
  { id: "transactions", label: "Transactions" },
  { id: "customers", label: "Customer Summary" },
  { id: "config", label: "Tax Config" },
];

const fmt = (v) => v?.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }) || "—";

export default function TaxationGst() {
  const [tab, setTab] = useState("tax");
  const [sub, setSub] = useState("dashboard");
  const [summary, setSummary] = useState(null);
  const [config, setConfig] = useState(null);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    api.get("/api/admin/tax/summary").then(({ data }) => setSummary(data));
    api.get("/api/admin/tax/config").then(({ data }) => setConfig(data));
  }, []);

  async function save() {
    const { data } = await api.put("/api/admin/tax/config", config);
    setConfig(data);
    setMsg("Saved");
    setTimeout(() => setMsg(""), 2000);
  }

  return (
    <AdminShell title="Taxation & GST">
      <PrimaryTabs tabs={TABS} value={tab} onChange={setTab} testIdPrefix="tax-tab" />
      <SecondaryTabs tabs={SUBTABS} value={sub} onChange={setSub} testIdPrefix="tax-sub" />

      <div className="mt-5">
        {sub === "dashboard" && summary && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-5">
              <KpiCard label="Total Fund Received" value={fmt(summary.total_volume_inr)} icon={DollarSign} variant="yellow" />
              <KpiCard label="Processed" value={fmt(summary.total_volume_inr * 0.95)} icon={TrendingUp} variant="green" />
              <KpiCard label="Pending" value={fmt(summary.total_volume_inr * 0.05)} icon={Receipt} variant="blue" />
              <KpiCard label="Total Tax Collected" value={fmt(summary.total_gst_inr + summary.total_tds_inr)} icon={Calculator} variant="red" />
              <KpiCard label="Total TDS (1%)" value={fmt(summary.total_tds_inr)} icon={Calculator} variant="purple" />
              <KpiCard label="Net Balance" value={fmt(summary.net_revenue_inr)} icon={DollarSign} variant="green" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Panel className="p-5">
                <h3 className="text-[14px] font-bold mb-3">Payment Mode Breakdown</h3>
                <div className="space-y-2 text-[13px]">
                  <Row label="UPI" value="₹4,50,000" pct={45} />
                  <Row label="IMPS" value="₹2,80,000" pct={28} />
                  <Row label="NEFT" value="₹1,50,000" pct={15} />
                  <Row label="RTGS" value="₹1,20,000" pct={12} />
                </div>
              </Panel>
              <Panel className="p-5">
                <h3 className="text-[14px] font-bold mb-3">Recent Transactions</h3>
                <div className="text-[12px] text-[#8892A8]">Detailed transaction list — see <a href="/admin/inr" className="text-[#F0B83A] hover:underline">INR Payments module</a>.</div>
              </Panel>
            </div>
          </>
        )}

        {sub === "transactions" && (
          <Panel className="p-10 text-center text-[#8892A8] text-[13px]">Tax transaction breakdown — wires after Phase D INR transactions are live.</Panel>
        )}

        {sub === "customers" && (
          <Panel className="p-10 text-center text-[#8892A8] text-[13px]">Per-customer tax summary (PAN-wise) — wires once trading data exists.</Panel>
        )}

        {sub === "config" && config && (
          <Panel className="p-5 max-w-[480px] space-y-4" data-testid="tax-config">
            <Field label="TDS Rate (%) — India default 1%"><input type="number" step="0.1" value={config.tds_rate_pct} onChange={(e) => setConfig({ ...config, tds_rate_pct: Number(e.target.value) })} className="w-full h-[40px] px-3 bg-[#0A0F1F] border border-[#1E2845] rounded-[8px] text-[14px] tabular-nums" /></Field>
            <Field label="GST Rate (%) — India default 18%"><input type="number" step="0.1" value={config.gst_rate_pct} onChange={(e) => setConfig({ ...config, gst_rate_pct: Number(e.target.value) })} className="w-full h-[40px] px-3 bg-[#0A0F1F] border border-[#1E2845] rounded-[8px] text-[14px] tabular-nums" /></Field>
            <Field label="PAN Required Above (₹)"><input type="number" value={config.pan_required_above_inr} onChange={(e) => setConfig({ ...config, pan_required_above_inr: Number(e.target.value) })} className="w-full h-[40px] px-3 bg-[#0A0F1F] border border-[#1E2845] rounded-[8px] text-[14px] tabular-nums" /></Field>
            <Btn onClick={save} data-testid="save-tax"><Save size={14} /> Save</Btn>
            {msg && <span className="text-[#10B981] text-[13px] ml-3">{msg}</span>}
          </Panel>
        )}
      </div>
    </AdminShell>
  );
}

function Row({ label, value, pct }) {
  return (
    <div>
      <div className="flex justify-between mb-1"><span>{label}</span><span className="font-bold">{value}</span></div>
      <div className="h-1.5 bg-[#0A0F1F] rounded-full overflow-hidden"><div className="h-full bg-[#F0B83A]" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}
function Field({ label, children }) {
  return <label className="block"><span className="block text-[10px] uppercase tracking-wider text-[#8892A8] mb-1.5">{label}</span>{children}</label>;
}
